import { useEffect } from 'react';
import { JsonValue } from 'type-fest';

import socketSingleton from 'services/webSocketService';

export type CallbackFuncType = (message: JsonValue) => void;
type CallbackType =
  | CallbackFuncType
  | CallbackFuncType[]
  | Record<string, CallbackFuncType>;

const buildCallback = (cb: CallbackType): CallbackFuncType => {
  if (typeof cb === 'function') {
    return cb;
  }

  if (Array.isArray(cb)) {
    return (message: JsonValue) => {
      for (const func of cb) {
        func(message);
      }
    };
  }

  return (message: JsonValue) => {
    for (const func of Object.values(cb)) {
      func(message);
    }
  };
};

/**
 * Subscribe a callback to listen for a topic.
 *
 * WARNING: This will unsubscribe and re-subscribe to the topic every time the
 * topic or callback function changes. So make sure your callback and topic change
 * somewhat in unison.
 *
 * You can specify an unsubscribeDebounceTime to prevent flickering of subscribe
 * and unsubscribe messaged if your callback does need to change.
 *
 * Alternatively, you can make your callback a useRef object to avoid the react lifecycle
 * updates entirely. This is the recommended approach whenever possible.
 *
 * @param topic - The topic to subscribe to on the websocket.
 * @param cb - The callback when a message is published to the topic. Either a function, object, or array of functions.
 * @param unsubscribeDebounceTime - time to debounce unsubscription calls - useful if the callback changes frequently
 */
export const useSubscribeWebsocket = (
  topic: string | null,
  cb: CallbackType,
  unsubscribeDebounceTime = 0,
) => {
  useEffect(() => {
    if (topic) {
      const { unsubscribe } = socketSingleton.subscribe(
        topic,
        buildCallback(cb),
        unsubscribeDebounceTime,
      );
      return unsubscribe;
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => {};
  }, [topic, cb, unsubscribeDebounceTime]);
};
